// Appearance provider: owns the color-scheme preference, keeps it in sync with
// the OS when set to System, and applies semantic tokens before children paint.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useMantineColorScheme } from '@mantine/core';
import { readColorScheme, saveColorScheme, type ColorSchemePreference } from '@/lib/preferences';
import { isMobileBrowser } from '@/lib/platform';
import { applyThemeVariables, blackTheme, lightTheme } from './tokens';

interface AppearanceContextValue {
  preference: ColorSchemePreference;
  resolved: 'light' | 'dark';
  setPreference: (preference: ColorSchemePreference) => void;
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

export function AppearanceProvider({ children }: { children: ReactNode }): ReactNode {
  const { setColorScheme } = useMantineColorScheme();
  const [preference, setPreferenceState] = useState<ColorSchemePreference>(() => readColorScheme());
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  // Track OS changes so "System" follows it live.
  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (event: MediaQueryListEvent): void => setSystemDark(event.matches);
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, []);

  // A phone browser tab only shows how to install the app and offers no theme
  // control, so it follows the system whatever was saved before.
  const [followSystem] = useState(() => isMobileBrowser());
  const effective: ColorSchemePreference = followSystem ? 'auto' : preference;
  const resolved: 'light' | 'dark' =
    effective === 'auto'
      ? systemDark
        ? 'dark'
        : 'light'
      : effective === 'dark'
        ? 'dark'
        : 'light';

  const appliedScheme = useRef<'light' | 'dark' | null>(null);
  useEffect(() => {
    setColorScheme(resolved);
    document.documentElement.setAttribute('data-mantine-color-scheme', resolved);
    document.documentElement.setAttribute('data-sensing-color-scheme', preference);
    applyThemeVariables(resolved === 'dark' ? blackTheme : lightTheme);
    // Only a change after the first paint needs the Safari workaround; the
    // initial scheme is set before the page renders (see index.html).
    if (appliedScheme.current !== null && appliedScheme.current !== resolved) {
      restyleDocument();
    }
    appliedScheme.current = resolved;
  }, [resolved, preference, setColorScheme]);

  const setPreference = useCallback((next: ColorSchemePreference) => {
    setPreferenceState(next);
    saveColorScheme(next);
  }, []);

  const value = useMemo<AppearanceContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

/**
 * Force every element to recompute its style.
 *
 * Mantine scopes its scheme colours with selectors such as
 * `:where([data-mantine-color-scheme='dark']) .m_x`. Safari does not always
 * restyle existing descendants when that attribute changes on `<html>`, so after
 * a switch parts of the page kept the previous scheme until reload. Taking the
 * document out of layout and back in, within one task, makes WebKit rebuild
 * every element's style; nothing is painted in between, and the window scroll
 * position is restored.
 */
function restyleDocument(): void {
  const root = document.documentElement;
  const { scrollX, scrollY } = window;
  const previous = root.style.display;
  root.style.display = 'none';
  // Reading layout forces the style change to be applied before restoring.
  void root.offsetHeight;
  root.style.display = previous;
  window.scrollTo(scrollX, scrollY);
}

/** Access the appearance preference and resolved scheme. */
export function useAppearance(): AppearanceContextValue {
  const value = useContext(AppearanceContext);
  if (!value) {
    throw new Error('useAppearance must be used inside AppearanceProvider');
  }
  return value;
}
